/**
 * /admin/platform/tenants — list, provision, suspend, reactivate,
 * delete tenants. Sensitive actions trigger the MFA prompt.
 */

import { type FormEvent, useEffect, useState } from "react";
import {
  adminApi,
  type AdminApiError,
  type TenantSummary,
} from "./api";
import { MfaPrompt } from "./MfaPrompt";

type Pending =
  | { kind: "suspend" | "reactivate" | "delete"; tenant: TenantSummary }
  | null;

export function TenantsPage() {
  const [tenants, setTenants] = useState<TenantSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<Pending>(null);

  async function reload() {
    setError(null);
    try {
      const result = await adminApi.listTenants();
      setTenants(result.tenants);
    } catch (exc) {
      const apiErr = exc as AdminApiError;
      setError(apiErr.message ?? "Could not load tenants.");
      setTenants([]);
    }
  }

  useEffect(() => {
    void reload();
  }, []);

  async function executePending(code: string) {
    if (pending === null) {
      return;
    }
    if (pending.kind === "suspend") {
      await adminApi.suspendTenant(pending.tenant.id, code);
    } else if (pending.kind === "reactivate") {
      await adminApi.reactivateTenant(pending.tenant.id, code);
    } else {
      await adminApi.deleteTenant(pending.tenant.id, code);
    }
    setPending(null);
    await reload();
  }

  return (
    <div className="admin-page">
      <header className="admin-page-head">
        <h1>Tenants</h1>
        <p className="admin-page-sub">
          Provision and manage every tenant on the platform. Sensitive
          actions require a fresh authenticator code.
        </p>
      </header>

      <ProvisionTenantForm onCreated={() => void reload()} />

      {error !== null && (
        <div className="auth-error" role="alert">
          {error}
        </div>
      )}

      <table className="admin-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Slug</th>
            <th>Status</th>
            <th>Members</th>
            <th>Created</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {tenants === null ? (
            <tr>
              <td colSpan={6} className="admin-empty">Loading…</td>
            </tr>
          ) : tenants.length === 0 ? (
            <tr>
              <td colSpan={6} className="admin-empty">No tenants yet.</td>
            </tr>
          ) : (
            tenants.map((t) => (
              <tr key={t.id}>
                <td>{t.name}</td>
                <td><code>{t.slug}</code></td>
                <td>
                  {t.suspended ? (
                    <span className="admin-pill admin-pill-warn">suspended</span>
                  ) : (
                    <span className="admin-pill admin-pill-ok">active</span>
                  )}
                </td>
                <td>{t.member_count}</td>
                <td>{new Date(t.created_at).toLocaleDateString()}</td>
                <td className="admin-row-actions">
                  {t.suspended ? (
                    <button
                      type="button"
                      className="btn btn-sm"
                      onClick={() => setPending({ kind: "reactivate", tenant: t })}
                    >
                      Reactivate
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-sm"
                      onClick={() => setPending({ kind: "suspend", tenant: t })}
                    >
                      Suspend
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-sm btn-danger"
                    onClick={() => setPending({ kind: "delete", tenant: t })}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      <MfaPrompt
        open={pending !== null}
        title={
          pending === null
            ? ""
            : pending.kind === "suspend"
              ? `Suspend ${pending.tenant.name}`
              : pending.kind === "reactivate"
                ? `Reactivate ${pending.tenant.name}`
                : `Delete ${pending.tenant.name}`
        }
        description={
          pending?.kind === "delete"
            ? "This permanently deletes the tenant and all of its data. Enter your authenticator code to confirm."
            : "Enter your current authenticator code to confirm this sensitive action."
        }
        onCancel={() => setPending(null)}
        onConfirm={async (code) => {
          await executePending(code);
        }}
      />
    </div>
  );
}

function ProvisionTenantForm({ onCreated }: { onCreated: () => void }) {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await adminApi.provisionTenant(name, slug);
      setName("");
      setSlug("");
      onCreated();
    } catch (exc) {
      const apiErr = exc as AdminApiError;
      setError(apiErr.message ?? "Could not create tenant.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="admin-card" onSubmit={onSubmit} noValidate>
      <h2 className="admin-card-title">Provision new tenant</h2>
      <div className="admin-inline-fields">
        <div className="field">
          <label htmlFor="new-tenant-name">Display name</label>
          <input
            id="new-tenant-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Acme Constructions"
            required
          />
        </div>
        <div className="field">
          <label htmlFor="new-tenant-slug">URL slug</label>
          <input
            id="new-tenant-slug"
            type="text"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="acme"
            pattern="[a-z0-9-]+"
            required
          />
        </div>
      </div>
      {error !== null && (
        <div className="auth-error" role="alert">
          {error}
        </div>
      )}
      <div className="actions">
        <button type="submit" className="btn btn-primary btn-sm" disabled={busy}>
          {busy ? "Provisioning…" : "Create tenant"}
        </button>
      </div>
    </form>
  );
}
